import React, { useState } from 'react';
import {
    Mail,
    ArrowRight,
    Home,
    CheckCircle,
    AlertCircle,
    Loader,
    ArrowLeft,
    Clock,
    Shield,
    Key
} from 'lucide-react';
import {useNavigate} from "react-router-dom";
import {API_CONSTANTS} from "../../Constants";
import SEO from "../../Components/Common/SEO";

const ForgotPassword = () => {
    const navigate = useNavigate();

    const handleNavigate = (path) => {
        console.log('Navigate to:', path);
        navigate(path);
    };

    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState(null); // null, 'success', 'error'
    const [error, setError] = useState('');
    const [resendError, setResendError] = useState('');
    const [countdown, setCountdown] = useState(0);

    const validateEmail = (email) => {
        return /\S+@\S+\.\S+/.test(email);
    };

    const startResendCountdown = () => {
        setCountdown(60);
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // The backend always responds 200 + success:true, whether or not the
    // email matches an account, to avoid leaking which emails are registered.
    // So the only branches here are "request failed validation/network" vs "request accepted".
    const requestPasswordReset = async () => {
        const response = await fetch(API_CONSTANTS.BASE_URL + '/auth/forgot-password', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({email})
        });
        return response.json();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Email address is required');
            return;
        }
        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        setIsLoading(true);
        setError('');
        setStatus(null);

        try {
            const data = await requestPasswordReset();
            if (data.success) {
                setStatus('success');
                startResendCountdown();
            } else if (data.field === 'email') {
                setError(data.message || 'Please enter a valid email address');
            } else {
                setStatus('error');
            }
        } catch (err) {
            setStatus('error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;
        setIsLoading(true);
        setResendError('');

        try {
            const data = await requestPasswordReset();
            if (data.success) {
                startResendCountdown();
            } else {
                setResendError('Failed to resend email. Please try again.');
            }
        } catch (err) {
            setResendError('Failed to resend email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const steps = [
        {
            icon: Mail,
            title: 'Enter your email',
            description: 'We\'ll send a reset link to your email address'
        },
        {
            icon: Clock,
            title: 'Check your inbox',
            description: 'Click the link in the email we sent you'
        },
        {
            icon: Key,
            title: 'Create new password',
            description: 'Choose a strong password for your account'
        }
    ];

    return (
        <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <SEO title="Forgot Password" canonicalPath="/forgot-password" noindex />
            {/* Background Pattern - Fixed to cover full page */}
            <div className="fixed inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-highland-50 to-karibu-200" />
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }} />
            </div>

            <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                {/* Header with left-right alignment */}
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => handleNavigate('/')}
                        className="flex items-center text-sm text-soil-400 hover:text-soil-600"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Home
                    </button>
                    <div className="flex items-center text-sm text-soil-600">
                        <Clock className="w-4 h-4 mr-1" />
                        Step 1 of 3
                    </div>
                </div>

                <div className="text-center">
                    <div className="mx-auto h-16 w-16 bg-highland-500 rounded-2xl flex items-center justify-center mb-6">
                        <Key className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex items-center justify-between mb-4">
                        <div></div>
                        <h2 className="font-display text-3xl font-bold text-soil-900">Forgot Password?</h2>
                        <div></div>
                    </div>
                    <p className="text-sm text-soil-600">
                        No worries! Enter your email and we'll send you a reset link.
                    </p>
                </div>
            </div>

            <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl rounded-xl sm:px-10">
                    {/* Success State */}
                    {status === 'success' ? (
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-highland-50 mb-6">
                                <CheckCircle className="h-8 w-8 text-highland-500" />
                            </div>

                            <h3 className="font-display text-lg font-medium text-soil-900 mb-2">Email Sent!</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                We've sent a password reset link to <strong>{email}</strong>.
                                Check your inbox and follow the instructions.
                            </p>

                            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4 mb-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start">
                                        <Shield className="h-5 w-5 text-highland-500 mt-0.5 mr-3" />
                                        <div className="text-left">
                                            <h4 className="text-sm font-medium text-highland-700 mb-1">Security Tips:</h4>
                                            <ul className="text-sm text-highland-600 space-y-1">
                                                <li>• Check your spam folder</li>
                                                <li>• Link expires in 1 hour</li>
                                                <li>• Don't share the reset link</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="ml-4 text-xs text-highland-500 font-medium">
                                        Secure
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between space-x-3">
                                    <button
                                        onClick={handleResend}
                                        disabled={countdown > 0 || isLoading}
                                        className="flex-1 bg-soil-100 hover:bg-soil-100/70 disabled:bg-soil-50 disabled:text-soil-400 text-soil-600 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader className="w-4 h-4 mr-2 animate-spin inline" />
                                                Sending...
                                            </>
                                        ) : countdown > 0 ? (
                                            `Resend (${countdown}s)`
                                        ) : (
                                            'Resend Email'
                                        )}
                                    </button>

                                    <button
                                        onClick={() => handleNavigate('/login')}
                                        className="flex-1 bg-highland-500 hover:bg-highland-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                                    >
                                        Back to Login
                                    </button>
                                </div>
                                {resendError && (
                                    <p className="text-sm text-murram-600">{resendError}</p>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Form State */
                        <div>
                            {/* Error Message */}
                            {status === 'error' && (
                                <div className="mb-6 p-4 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <AlertCircle className="w-5 h-5 text-murram-500" />
                                        <p className="text-murram-600">Something went wrong. Please try again.</p>
                                    </div>
                                    <button
                                        onClick={() => setStatus(null)}
                                        className="text-murram-500 hover:text-murram-600"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}

                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <label htmlFor="email" className="text-sm font-medium text-soil-600">
                                        Email Address
                                    </label>
                                    <span className="text-xs text-soil-400">Required</span>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-soil-400" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setError('');
                                        }}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent ${
                                            error ? 'border-murram-500' : 'border-soil-100'
                                        }`}
                                        placeholder="Enter your email address"
                                    />
                                    {email && validateEmail(email) && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                            <CheckCircle className="h-5 w-5 text-highland-500" />
                                        </div>
                                    )}
                                </div>
                                {error && (
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-sm text-murram-600">{error}</p>
                                        <button
                                            onClick={() => setError('')}
                                            className="text-xs text-soil-400 hover:text-soil-600"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between space-x-4 mb-6">
                                <button
                                    onClick={() => handleNavigate('/login')}
                                    className="flex items-center text-sm font-medium text-highland-500 hover:text-highland-600"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    Back to Login
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading || !email}
                                    className="bg-highland-500 hover:bg-highland-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader className="w-5 h-5 animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Send Reset Link</span>
                                            <ArrowRight className="w-5 h-5" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* How it Works Section with left-right alignment */}
                {status !== 'success' && (
                    <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-lg font-medium text-soil-900">How it works</h3>
                            <span className="text-xs text-soil-400 bg-soil-100 px-2 py-1 rounded">3 Steps</span>
                        </div>
                        <div className="space-y-4">
                            {steps.map((step, index) => {
                                const Icon = step.icon;
                                return (
                                    <div key={index} className="flex items-center justify-between">
                                        <div className="flex items-start space-x-3 flex-1">
                                            <div className="flex-shrink-0">
                                                <div className="w-8 h-8 bg-highland-50 rounded-lg flex items-center justify-center">
                                                    <Icon className="w-4 h-4 text-highland-500" />
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-medium text-soil-900">{step.title}</h4>
                                                <p className="text-sm text-soil-600">{step.description}</p>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            <span className="inline-flex items-center justify-center w-6 h-6 bg-soil-100 rounded-full text-xs font-medium text-soil-600 font-mono">
                                                {index + 1}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Footer with left-right alignment */}
                <div className="mt-6 flex items-center justify-between">
                    <div className="text-center">
                        <p className="text-sm text-soil-600 mb-1">Need help?</p>
                        <button
                            onClick={() => handleNavigate('/contact')}
                            className="text-sm font-medium text-highland-500 hover:text-highland-600"
                        >
                            Contact Support
                        </button>
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-soil-600 mb-1">Remember your password?</p>
                        <button
                            onClick={() => handleNavigate('/login')}
                            className="text-sm font-medium text-highland-500 hover:text-highland-600"
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;